/*
 * /board-game-bank — the banker's screen: every player's money in big
 * numbers, one tap to pay, the requests from the phones, the poker table,
 * the history with undo, the results.
 *
 * Rendered from the game on every change (a game is small: a replay of a
 * thousand entries takes a millisecond), so there is no second copy of any
 * number on this page that could drift from the journal.
 */
import * as L from './ledger.js';
import * as P from './poker.js';
import * as G from './game.js';
import * as H from './history.js';
import * as S from './sheet.js';
import { h, t, $, clear, put, money, money2, num, mmss, qr, copyText, soundEnabled } from './ui.js';

const { BANK, POT } = L;
const HISTORY_PAGE = 60;

export function render(app) {
    const g = app.game;
    if (!g) return;
    const rp = L.replay(g);
    bar(app, g);
    invite(app, g);
    requests(app, g, rp);
    if (g.kind === 'poker') poker(app, g, rp); else { $('bgbPoker').hidden = true; clear($('bgbPoker')); }
    players(app, g, rp);
    bank(app, g, rp);
    settings(app, g, rp);
    history(app, g, rp);
    results(app, g, rp);
}

/* ── the bar ──────────────────────────────────────────────────────────────── */

function bar(app, g) {
    const el = clear($('bgbBar'));
    const last = lastUndoable(g);
    const undo = h('button', {
        type: 'button', class: 'bgb-tb', id: 'bgbUndo', disabled: !last, 'aria-keyshortcuts': 'Z',
        title: last ? t('ui.undo_what', { what: last }) : t('ui.undo_nothing'),
        on: { click: () => app.undo() },
    }, h('span', { 'aria-hidden': 'true', text: '↶' }), h('span', { class: 'bgb-tb-l', text: t('ui.undo') }));
    const phones = h('button', {
        type: 'button', class: 'bgb-tb', id: 'bgbPhones', 'aria-pressed': String(!!app.hostRoom), 'aria-controls': 'bgbInvite',
        on: { click: () => app.toggleRoom() },
    }, h('span', { 'aria-hidden': 'true', text: '📱' }), h('span', { class: 'bgb-tb-l', text: t('ui.phones') }),
    app.hostRoom ? h('span', { class: 'bgb-badge', text: String(app.online.size) }) : null);
    const add = h('button', { type: 'button', class: 'bgb-tb', id: 'bgbAddLate', disabled: g.players.length >= L.MAX_PLAYERS || !!g.ended, on: { click: () => S.openAdd(app) } },
        h('span', { 'aria-hidden': 'true', text: '➕' }), h('span', { class: 'bgb-tb-l', text: t('ui.player') }));
    const snd = h('button', {
        type: 'button', class: 'bgb-tb bgb-tb-i', id: 'bgbSound', 'aria-pressed': String(soundEnabled()), title: t('ui.sound'), 'aria-label': t('ui.sound'),
        on: { click: () => app.toggleSound() },
    }, h('span', { 'aria-hidden': 'true', text: soundEnabled() ? '🔊' : '🔇' }));
    const fs = h('button', { type: 'button', class: 'bgb-tb bgb-tb-i', id: 'bgbFs', title: t('ui.fullscreen'), 'aria-label': t('ui.fullscreen'), on: { click: () => app.fullscreen() } },
        h('span', { 'aria-hidden': 'true', text: '⛶' }));
    const end = h('button', { type: 'button', class: 'bgb-tb', id: 'bgbEnd', hidden: !!g.ended, on: { click: () => app.endGame() } },
        h('span', { 'aria-hidden': 'true', text: '🏁' }), h('span', { class: 'bgb-tb-l', text: t('ui.end_game') }));
    put(el, h('div', { class: 'bgb-bar-t' }, h('b', { text: t('preset.' + g.preset) }), h('span', { class: 'bgb-hint', text: ' · ' + t('ui.players_n', { n: g.players.length }) })),
        h('div', { class: 'bgb-bar-b' }, undo, phones, add, snd, fs, end));
}

function lastUndoable(g) {
    if (g.kind === 'poker' && g.poker.hand) return g.poker.snaps.length ? t('pk.undo_action') : '';
    for (let i = g.log.length - 1; i >= 0; i--) {
        const e = g.log[i];
        if (!e.void) return H.describe(g, e, L.replay(g)).what;
    }
    return '';
}

/* ── the room (phones) ────────────────────────────────────────────────────── */

function invite(app, g) {
    const el = $('bgbInvite');
    if (!app.hostRoom) { el.hidden = true; clear(el); return; }
    el.hidden = false;
    const code = app.hostRoom.code;
    const url = app.inviteUrl(code);
    // keep the QR canvas across renders: redrawing it on every payment would flicker
    let qrBox = el.querySelector('.bgb-qr');
    const keepQr = qrBox && qrBox.getAttribute('data-url') === url && qrBox.firstChild;
    const oldQr = keepQr ? qrBox : null;
    clear(el);
    if (!oldQr) {
        qrBox = h('div', { class: 'bgb-qr', 'data-url': url });
        const draw = () => qr(qrBox, url, 196);
        if (!draw()) setTimeout(draw, 800);
    } else qrBox = oldQr;
    const link = h('input', { type: 'text', class: 'bgb-in bgb-link-in', id: 'bgbInviteUrl', readonly: true, value: url, 'aria-label': t('inv.link') });
    const copy = h('button', {
        type: 'button', class: 'lg-btn lg-btn-ghost lg-btn-sm', text: t('inv.copy'),
        on: { click: () => copyText(url).then((ok) => { if (ok) app.say(t('inv.copied'), 'success'); else { link.select(); } }) },
    });
    const share = navigator.share ? h('button', {
        type: 'button', class: 'lg-btn lg-btn-ghost lg-btn-sm', text: t('inv.share'),
        on: { click: () => { navigator.share({ title: t('inv.share_title'), text: t('inv.share_text', { code: code.toUpperCase() }), url }).catch(() => { /* closed */ }); } },
    }) : null;
    const rp = L.replay(g);
    const seats = h('ul', { class: 'bgb-seats' }, g.players.filter((p) => rp.joined[p.id]).map((p) => {
        const on = app.online.has(p.id);
        return h('li', { class: 'bgb-seat' + (on ? ' is-on' : p.dev ? ' is-off' : '') },
            h('span', { class: 'bgb-dot', 'aria-hidden': 'true' }),
            h('span', { text: p.token + ' ' + p.name }),
            h('span', { class: 'bgb-hint', text: on ? t('inv.seat_on') : p.dev ? t('inv.seat_off') : t('inv.seat_free') }));
    }));
    const statusText = app.roomStatus === 'open' ? (app.online.size ? t('inv.status_n', { n: app.online.size }) : t('inv.status_wait'))
        : app.roomStatus === 'retry' ? t('inv.status_retry') : app.roomStatus === 'error' ? t('inv.status_error') : t('inv.status_opening');
    put(el, 
        h('div', { class: 'bgb-inv-grid' },
            qrBox,
            h('div', { class: 'bgb-inv-main' },
                h('div', { class: 'bgb-hint', text: t('inv.code') }),
                h('div', { class: 'bgb-code', id: 'bgbCode', text: code.toUpperCase() }),
                h('div', { class: 'bgb-row' }, link, copy, share),
                h('p', { class: 'bgb-status is-' + (app.roomStatus || 'opening'), id: 'bgbRoomStatus', role: 'status', text: statusText }),
                h('p', { class: 'bgb-hint', text: t('inv.hint') }))),
        seats,
        h('div', { class: 'bgb-row' }, h('button', { type: 'button', class: 'lg-btn lg-btn-ghost lg-btn-sm', id: 'bgbRoomClose', text: t('inv.close'), on: { click: () => app.toggleRoom() } })));
}

/* ── requests from the phones ─────────────────────────────────────────────── */

function requests(app, g, rp) {
    const el = $('bgbReqs');
    const open = g.requests.filter((r) => r.st === 'open');
    if (!open.length) { el.hidden = true; clear(el); return; }
    el.hidden = false;
    put(clear(el), h('div', { class: 'bgb-sec-h', text: t('req.title', { n: open.length }) }),
        h('ul', { class: 'bgb-reqs' }, open.map((r) => {
            const amount = r.from === POT && !r.amount ? t('req.pot_all', { x: money(rp.pot, g.cur) }) : money(r.amount, g.cur);
            return h('li', { class: 'bgb-req', 'data-rid': r.id },
                h('span', { class: 'bgb-req-t' },
                    h('b', { text: S.acctLabel(g, r.to) }), ' ', t('req.asks'), ' ', h('b', { text: S.acctLabel(g, r.from) }),
                    ' · ', h('b', { class: 'bgb-req-amt', text: amount }),
                    r.note ? h('span', { class: 'bgb-hint', text: ' · ' + S.noteText(r.note) }) : null),
                h('span', { class: 'bgb-req-b' },
                    h('button', { type: 'button', class: 'lg-btn lg-btn-sm', text: r.from === BANK || r.from === POT ? t('req.give') : t('req.pay_for'), on: { click: () => app.answer(r.id, true) } }),
                    h('button', { type: 'button', class: 'lg-btn lg-btn-ghost lg-btn-sm', text: t('req.decline'), on: { click: () => app.answer(r.id, false) } })));
        })));
}

/* ── players ──────────────────────────────────────────────────────────────── */

function players(app, g, rp) {
    const el = clear($('bgbPlayers'));
    const hand = g.kind === 'poker' ? g.poker.hand : null;
    const seen = app.flash || {};
    for (const p of g.players) {
        if (!rp.joined[p.id]) continue;
        const out = !!rp.out[p.id];
        const bal = rp.bal[p.id] || 0;
        const x = hand && hand.s[p.id];
        const stack = x ? x.stack : bal;
        const roles = hand ? P.roles(hand, p.id) : [];
        const turn = hand && hand.toAct === p.id;
        const badges = [];
        for (const r of roles) badges.push(h('span', { class: 'bgb-role bgb-role-' + r.toLowerCase(), text: r === 'D' ? t('pk.dealer_short') : r }));
        if (x && x.fold) badges.push(h('span', { class: 'bgb-state', text: t('pk.folded') }));
        else if (x && x.allin) badges.push(h('span', { class: 'bgb-state bgb-state-allin', text: t('pk.allin') }));
        if (out) badges.push(h('span', { class: 'bgb-state', text: g.kind === 'poker' ? t('pk.left') : t('ui.out') }));
        const busted = g.kind === 'poker' && !out && bal <= 0 && !x;
        if (busted) badges.push(h('span', { class: 'bgb-state bgb-state-allin', text: t('pk.busted') }));
        const phone = p.dev ? h('span', { class: 'bgb-phone' + (app.online.has(p.id) ? ' is-on' : ''), title: app.online.has(p.id) ? t('inv.seat_on') : t('inv.seat_off'), 'aria-label': app.online.has(p.id) ? t('inv.seat_on') : t('inv.seat_off'), text: '📱' }) : null;
        const main = h('button', {
            type: 'button', class: 'bgb-tile-main', 'data-pid': p.id,
            'aria-label': p.name + ', ' + money(stack, g.cur),
            on: { click: () => (g.kind === 'poker' || out ? S.openEdit(app, p.id) : S.openMoney(app, { pid: p.id, dir: 'pay' })) },
        },
        h('span', { class: 'bgb-tile-top' }, h('span', { class: 'bgb-tok', text: p.token, 'aria-hidden': 'true' }), h('span', { class: 'bgb-name', text: p.name }), phone),
        h('span', { class: 'bgb-bal' + (bal < 0 ? ' is-neg' : ''), 'data-bal': String(stack), text: money(stack, g.cur) }),
        x && (x.bet || x.total) ? h('span', { class: 'bgb-bet', text: t('pk.in_pot', { x: num(x.total) }) + (x.bet ? ' · ' + t('pk.bet_now', { x: num(x.bet) }) : '') }) : null,
        badges.length ? h('span', { class: 'bgb-badges' }, badges) : null);
        const tile = h('div', {
            class: 'bgb-tile' + (out ? ' is-out' : '') + (turn ? ' is-turn' : '') + (x && x.fold ? ' is-fold' : '') + (seen[p.id] ? ' is-flash' : ''),
            style: { '--pc': p.color }, 'data-pid': p.id,
        }, main);
        if (busted && !g.ended) {
            tile.classList.add('is-fold');
            put(tile, h('div', { class: 'bgb-tile-acts' }, h('button', { type: 'button', class: 'bgb-mini', 'data-rebuy': p.id, text: '➕ ' + t('ui.rebuy', { x: num(g.start) }), on: { click: () => app.run({ type: 'rebuy', pid: p.id }).then((r) => { if (!r.ok) app.say(app.errText(r), 'warning'); }) } })));
        }
        if (g.kind !== 'poker' && !out && !g.ended) {
            const row = h('div', { class: 'bgb-tile-acts' });
            if (g.salary > 0) put(row, h('button', { type: 'button', class: 'bgb-mini', 'data-salary': p.id, text: '+' + money(g.salary, g.cur), title: t('note.go'), 'aria-label': t('ui.salary_btn', { x: money(g.salary, g.cur) }) + ' — ' + p.name, on: { click: () => app.run({ type: 'salary', pid: p.id }) } }));
            put(row, h('button', { type: 'button', class: 'bgb-mini', text: '↓ ' + t('ui.dir_get'), 'aria-label': t('ui.dir_get') + ' — ' + p.name, on: { click: () => S.openMoney(app, { pid: p.id, dir: 'get' }) } }));
            put(tile, row);
        }
        put(el, tile);
    }
    app.flash = {};
}

/* ── bank row and the check ───────────────────────────────────────────────── */

function bank(app, g, rp) {
    const el = clear($('bgbBank'));
    const c = L.moneyCheck(g, rp);
    const bits = [];
    if (g.kind !== 'poker') {
        bits.push(h('span', { class: 'bgb-bank-i' }, '🏦 ', h('b', { text: t('ui.bank') }), ' · ', t('ui.bank_out', { x: money(c.bankOut, g.cur) }), ' · ', t('ui.bank_in', { x: money(c.bankIn, g.cur) })));
        if (g.jackpot) bits.push(h('span', { class: 'bgb-bank-i bgb-pot', id: 'bgbPot' }, '🅿️ ', h('b', { text: t('ui.pot') }), ' · ', h('b', { class: 'bgb-pot-v', text: money(rp.pot, g.cur) }), h('span', { class: 'bgb-hint', text: ' — ' + t('ui.pot_hint') })));
    } else {
        const rebuys = Object.keys(rp.rebuys).reduce((s, k) => s + rp.rebuys[k], 0);
        bits.push(h('span', { class: 'bgb-bank-i' }, '🎲 ', t('pk.bought', { x: num(c.expected), r: num(rebuys) })));
    }
    put(el, h('div', { class: 'bgb-bank-row' }, bits),
        h('p', { class: 'bgb-check' + (c.ok ? ' is-ok' : ' is-bad'), id: 'bgbCheck', text: H.checkLine(g, c) }));
}

/* ── settings during the game ─────────────────────────────────────────────── */

function settings(app, g) {
    const el = $('bgbSettings');
    const wasOpen = el.querySelector('details') ? el.querySelector('details').open : false;
    clear(el);
    const d = h('details', { class: 'bgb-set', open: wasOpen || null });
    put(d, h('summary', { text: '⚙ ' + t('set.title') }));
    const body = h('div', { class: 'bgb-set-b' });
    const sw = (key, id, label, hint) => {
        const cb = h('input', { type: 'checkbox', id, checked: !!g[key] });
        cb.addEventListener('change', async () => {
            const r = await app.run({ type: 'settings', [key]: cb.checked });
            if (!r.ok) { cb.checked = !cb.checked; app.say(app.errText(r), 'warning'); }
        });
        return h('label', { class: 'bgb-check', for: id }, cb, h('span', null, h('b', { text: label }), ' ', h('span', { class: 'bgb-hint', text: hint })));
    };
    if (g.kind !== 'poker') {
        const sal = h('input', { type: 'text', inputmode: 'numeric', class: 'bgb-in bgb-in-s', id: 'bgbSetSalary', value: String(g.salary || 0), 'aria-label': t('ui.salary') });
        sal.addEventListener('change', async () => {
            const v = sal.value.trim() === '0' ? 0 : L.parseAmount(sal.value);
            if (v == null) { sal.value = String(g.salary); return; }
            const r = await app.run({ type: 'settings', salary: v });
            if (!r.ok) app.say(app.errText(r), 'warning');
        });
        put(body, 
            sw('debt', 'bgbSetDebt', t('opt.debt'), t('opt.debt_d')),
            sw('jackpot', 'bgbSetPot', t('opt.jackpot'), t('opt.jackpot_d')),
            h('div', { class: 'bgb-field' }, h('label', { class: 'bgb-lbl', for: 'bgbSetSalary', text: t('ui.salary') }), sal));
    } else {
        put(body, levelsEditor(app, g));
        const price = h('input', { type: 'text', inputmode: 'numeric', class: 'bgb-in bgb-in-s', id: 'bgbSetPrice', value: String(g.poker.price || ''), 'aria-label': t('ui.price') });
        price.addEventListener('change', async () => {
            const v = price.value.trim() === '' || price.value.trim() === '0' ? 0 : L.parseAmount(price.value);
            if (v == null) { price.value = String(g.poker.price || ''); return; }
            await app.run({ type: 'settings', price: v });
        });
        put(body, h('div', { class: 'bgb-field' }, h('label', { class: 'bgb-lbl', for: 'bgbSetPrice', text: t('ui.price') + (g.poker.sym ? ', ' + g.poker.sym : '') }), price, h('div', { class: 'bgb-hint', text: t('ui.price_d') })));
    }
    put(body, sw('autoBank', 'bgbSetAuto', t('opt.autoBank'), t('opt.autoBank_d')));
    put(d, body);
    put(el, d);
}

function levelsEditor(app, g) {
    const pk = g.poker;
    const rows = pk.levels.map((l) => Object.assign({}, l));
    const tb = h('table', { class: 'bgb-levels' });
    const at = P.timerAt(pk.timer, pk.levels, Date.now());
    function paint() {
        put(clear(tb), h('tr', null, h('th', { text: '#' }), h('th', { text: t('ui.sb') }), h('th', { text: t('ui.bb') }), h('th', { text: t('ui.ante') }), h('th', { text: t('ui.level_min') }), h('th', { text: '' })));
        rows.forEach((l, i) => {
            const cell = (k) => {
                const inp = h('input', { type: 'text', inputmode: 'numeric', class: 'bgb-in bgb-in-xs', value: String(l[k]), 'aria-label': t(k === 'min' ? 'ui.level_min' : 'ui.' + k) + ' ' + (i + 1) });
                inp.addEventListener('change', () => { const v = +inp.value.replace(/\D/g, ''); if (v >= 0) l[k] = v; });
                return h('td', null, inp);
            };
            put(tb, h('tr', { class: i === at.lvl ? 'is-now' : null }, h('td', { text: String(i + 1) }), cell('sb'), cell('bb'), cell('ante'), cell('min'),
                h('td', null, h('button', { type: 'button', class: 'bgb-x', text: '×', 'aria-label': t('ui.level_remove', { n: i + 1 }), disabled: rows.length <= 1, on: { click: () => { rows.splice(i, 1); paint(); } } }))));
        });
    }
    paint();
    const add = h('button', {
        type: 'button', class: 'lg-btn lg-btn-ghost lg-btn-sm', text: '+ ' + t('ui.level_add'),
        on: { click: () => { const l = rows[rows.length - 1]; rows.push({ sb: l.sb * 2, bb: l.bb * 2, ante: l.ante ? l.ante * 2 : 0, min: l.min }); paint(); } },
    });
    const apply = h('button', {
        type: 'button', class: 'lg-btn lg-btn-sm', text: t('ui.levels_apply'),
        on: { click: async () => { const r = await app.run({ type: 'settings', levels: rows }); app.say(r.ok ? t('ui.levels_saved') : app.errText(r), r.ok ? 'success' : 'warning'); } },
    });
    return h('div', { class: 'bgb-field' }, h('div', { class: 'bgb-lbl', text: t('ui.blinds_structure') }), h('div', { class: 'bgb-tablewrap' }, tb), h('div', { class: 'bgb-row' }, add, apply));
}

/* ── history ──────────────────────────────────────────────────────────────── */

function history(app, g, rp) {
    const el = $('bgbHistory');
    const filterWas = app.hfilter || '';
    clear(el);
    const sel = h('select', { class: 'bgb-in bgb-in-s', id: 'bgbHFilter', 'aria-label': t('ui.history_filter') },
        h('option', { value: '', text: t('ui.history_all') }),
        g.players.map((p) => h('option', { value: p.id, text: p.token + ' ' + p.name, selected: filterWas === p.id ? 'selected' : null })));
    sel.addEventListener('change', () => { app.hfilter = sel.value; app.hshow = HISTORY_PAGE; history(app, g, rp); });
    const csvBtn = h('button', { type: 'button', class: 'lg-btn lg-btn-ghost lg-btn-sm', id: 'bgbCsv', text: 'CSV', on: { click: () => app.exportCsv() } });
    const pdfBtn = h('button', { type: 'button', class: 'lg-btn lg-btn-ghost lg-btn-sm', id: 'bgbPdf', text: 'PDF', on: { click: () => app.exportPdf() } });
    const lastLive = (() => { for (let i = g.log.length - 1; i >= 0; i--) if (!g.log[i].void) return g.log[i]; return null; })();
    let entries = g.log.slice().reverse();
    if (filterWas) entries = entries.filter((e) => {
        const d = H.describe(g, e, rp);
        return d.from === filterWas || d.to === filterWas || e.pid === filterWas || d.parts.some((x) => x[0] === filterWas) || (e.others && e.others.indexOf(filterWas) >= 0);
    });
    const shown = entries.slice(0, app.hshow || HISTORY_PAGE);
    const ol = h('ol', { class: 'bgb-hist', id: 'bgbHistList', reversed: true });
    for (const e of shown) {
        let action = null;
        if (!g.ended) {
            if (!e.void && (g.kind !== 'poker' || (e === lastLive && !g.poker.hand))) {
                action = h('button', { type: 'button', class: 'bgb-hbtn', title: t('ui.undo'), 'aria-label': t('ui.undo') + ': #' + e.id, text: '↶', on: { click: () => app.voidEntry(e.id) } });
            } else if (e.void && g.kind !== 'poker') {
                action = h('button', { type: 'button', class: 'bgb-hbtn', title: t('ui.restore'), 'aria-label': t('ui.restore') + ': #' + e.id, text: '↷', on: { click: () => app.restoreEntry(e.id) } });
            }
        }
        put(ol, H.row(g, e, rp, { me: filterWas || null, action }));
    }
    put(el, h('div', { class: 'bgb-hist-h' },
        h('h3', { class: 'bgb-h', text: t('ui.history') + ' · ' + g.log.filter((e) => !e.void).length }),
        h('div', { class: 'bgb-row' }, sel, csvBtn, pdfBtn)));
    if (!g.log.length) put(el, h('p', { class: 'bgb-hint', text: t('ui.history_empty') }));
    else put(el, ol);
    if (entries.length > shown.length) {
        put(el, h('button', { type: 'button', class: 'lg-btn lg-btn-ghost lg-btn-sm', text: t('ui.history_more', { n: entries.length - shown.length }), on: { click: () => { app.hshow = (app.hshow || HISTORY_PAGE) + HISTORY_PAGE * 3; history(app, g, rp); } } }));
    }
}

/* ── results ──────────────────────────────────────────────────────────────── */

export function resultsNode(g, rp) {
    const box = h('div', { class: 'bgb-results-b' });
    if (g.kind === 'poker') {
        const price = g.poker.price || 0;
        const nets = price ? L.pokerNets(g, price, rp) : null;
        const tb = h('table', { class: 'bgb-rtable' });
        put(tb, h('tr', null, h('th', { text: t('csv.player') }), h('th', { text: t('res.bought') }), h('th', { text: t('res.chips') }), h('th', { text: t('res.net') }), price ? h('th', { text: t('res.money') }) : null));
        const rows = g.players.map((p) => {
            const bought = g.start + (rp.rebuys[p.id] || 0);
            const now = (rp.bal[p.id] || 0) + (rp.cashed[p.id] || 0);
            return { p, bought, now, net: now - bought };
        }).sort((a, b) => b.net - a.net);
        for (const r of rows) {
            put(tb, h('tr', null, h('td', { text: r.p.token + ' ' + r.p.name }), h('td', { text: num(r.bought) }), h('td', { text: num(r.now) }),
                h('td', { class: r.net > 0 ? 'is-in' : r.net < 0 ? 'is-out' : null, text: (r.net > 0 ? '+' : '') + (r.net < 0 ? '−' : '') + num(Math.abs(r.net)) }),
                price ? h('td', { text: money2(nets[r.p.id], g.poker.sym || '') }) : null));
        }
        put(box, h('div', { class: 'bgb-tablewrap' }, tb));
        if (price) {
            const tx = L.settle(nets);
            put(box, h('h4', { class: 'bgb-h', text: t('res.who_pays') }));
            if (!tx.length) put(box, h('p', { class: 'bgb-hint', text: t('res.nobody') }));
            else put(box, h('ul', { class: 'bgb-settle' }, tx.map((x) => h('li', null,
                h('b', { text: H.name(g, x.from) }), ' → ', h('b', { text: H.name(g, x.to) }), ' · ', h('b', { text: money2(x.amount, g.poker.sym || '') })))));
        } else put(box, h('p', { class: 'bgb-hint', text: t('res.price_hint') }));
    } else {
        // standing: players still in by money, then the bankrupt ones, last out last
        const outOrder = [];
        for (const e of g.log) if (!e.void && e.type === 'bankrupt') outOrder.push(e.pid);
        const alive = g.players.filter((p) => rp.joined[p.id] && !rp.out[p.id]).sort((a, b) => (rp.bal[b.id] || 0) - (rp.bal[a.id] || 0));
        const gone = outOrder.slice().reverse().map((id) => L.player(g, id)).filter(Boolean);
        const ol = h('ol', { class: 'bgb-rank' });
        [...alive, ...gone].forEach((p, i) => {
            put(ol, h('li', { class: 'bgb-rank-i', style: { '--pc': p.color } },
                h('span', { class: 'bgb-rank-n', text: i === 0 ? '🏆' : String(i + 1) }),
                h('span', { class: 'bgb-rank-p', text: p.token + ' ' + p.name }),
                h('span', { class: 'bgb-rank-v', text: rp.out[p.id] ? t('ui.out') : money(rp.bal[p.id] || 0, g.cur) }),
                h('span', { class: 'bgb-hint bgb-rank-s', text: t('res.flow', { in: money(rp.stats[p.id].in, g.cur), out: money(rp.stats[p.id].out, g.cur) }) })));
        });
        put(box, ol);
    }
    return box;
}

function results(app, g, rp) {
    const el = $('bgbResults');
    if (!g.ended) { el.hidden = true; clear(el); return; }
    el.hidden = false;
    put(clear(el), 
        h('h3', { class: 'bgb-h', text: '🏁 ' + t('res.title') }),
        resultsNode(g, rp),
        h('div', { class: 'bgb-row' },
            h('button', { type: 'button', class: 'lg-btn', id: 'bgbResPdf', text: t('res.pdf'), on: { click: () => app.exportPdf() } }),
            h('button', { type: 'button', class: 'lg-btn lg-btn-ghost', id: 'bgbResCsv', text: t('res.csv'), on: { click: () => app.exportCsv() } }),
            h('button', { type: 'button', class: 'lg-btn lg-btn-ghost', id: 'bgbReopen', text: t('res.reopen'), on: { click: () => app.run({ type: 'reopen' }) } }),
            h('button', { type: 'button', class: 'lg-btn lg-btn-ghost', id: 'bgbNewGame', text: t('res.new'), on: { click: () => app.newGame() } })));
}

/* ── poker ────────────────────────────────────────────────────────────────── */

export function streetName(h) {
    if (!h) return '';
    if (h.street === 'show') return h.done ? t('pk.won_by_fold') : t('pk.showdown');
    return t('pk.street_' + h.street);
}

/** The clock block (host: with controls; phone: read only). */
export function clockNode(app, g, controls) {
    const pk = g.poker;
    const now = Date.now();
    const at = P.timerAt(pk.timer, pk.levels, now);
    const lv = pk.levels[at.lvl];
    const next = pk.levels[at.lvl + 1];
    const box = h('div', { class: 'bgb-clock' + (pk.timer.on ? ' is-on' : '') });
    put(box, 
        h('div', { class: 'bgb-clock-lv', text: t('pk.level', { n: at.lvl + 1 }) + ' · ' + t('pk.blinds', { sb: num(lv.sb), bb: num(lv.bb) }) + (lv.ante ? ' · ' + t('pk.ante_x', { x: num(lv.ante) }) : '') }),
        h('div', { class: 'bgb-clock-t', 'data-clock': '1', text: at.over ? '—' : mmss(at.remain) }),
        h('div', { class: 'bgb-clock-next', text: next ? t('pk.next', { sb: num(next.sb), bb: num(next.bb) }) : t('pk.last_level') }));
    if (controls) {
        put(box, h('div', { class: 'bgb-clock-b' },
            h('button', { type: 'button', class: 'bgb-tb bgb-tb-i', 'aria-label': t('pk.level_prev'), title: t('pk.level_prev'), text: '⏮', disabled: at.lvl === 0, on: { click: () => app.run({ type: 'timer', op: 'prev' }) } }),
            h('button', { type: 'button', class: 'bgb-tb', id: 'bgbClockGo', text: pk.timer.on ? '⏸ ' + t('pk.pause') : '▶ ' + t('pk.start_clock'), on: { click: () => app.run({ type: 'timer', op: pk.timer.on ? 'pause' : 'start' }) } }),
            h('button', { type: 'button', class: 'bgb-tb bgb-tb-i', 'aria-label': t('pk.level_next'), title: t('pk.level_next'), text: '⏭', disabled: !next, on: { click: () => app.run({ type: 'timer', op: 'next' }) } })));
    }
    return box;
}

/** Action buttons for the player to act (the banker, or that player's phone). */
export function actionNode(app, g, pid) {
    const hd = g.poker.hand;
    const o = P.options(hd, pid);
    if (!o) return null;
    const pot = P.potTotal(hd);
    const btn = (label, cls, fn, id) => h('button', { type: 'button', class: 'lg-btn bgb-pkb ' + (cls || ''), id: id || null, text: label, on: { click: fn } });
    const row = h('div', { class: 'bgb-pkacts' });
    put(row, btn(t('pk.fold'), 'lg-btn-ghost', () => app.act(pid, 'fold'), 'bgbActFold'));
    if (o.check) put(row, btn(t('pk.check'), '', () => app.act(pid, 'check'), 'bgbActCheck'));
    else put(row, btn(t('pk.call_x', { x: num(o.callAmt) }) + (o.callAmt === hd.s[pid].stack ? ' · ' + t('pk.allin') : ''), '', () => app.act(pid, 'call'), 'bgbActCall'));
    if (o.raise) {
        put(row, btn(o.bet ? t('pk.bet') + '…' : t('pk.raise') + '…', '', async () => {
            const to = await S.askRaise(app, pid, o, pot);
            if (to) app.act(pid, 'raise', to);
        }, 'bgbActRaise'));
        put(row, btn(t('pk.allin') + ' · ' + num(o.allinTo), 'bgb-allin', () => app.act(pid, 'allin'), 'bgbActAllin'));
    }
    return row;
}

function poker(app, g, rp) {
    const el = $('bgbPoker');
    el.hidden = false;
    clear(el);
    const pk = g.poker;
    const hd = pk.hand;
    put(el, clockNode(app, g, !g.ended));
    const box = h('div', { class: 'bgb-hand' });
    if (!hd) {
        const seats = g.players.filter((p) => rp.joined[p.id] && !rp.out[p.id]).map((p) => p.id);
        const stacks = {};
        for (const p of seats) stacks[p] = rp.bal[p] || 0;
        const bl = G.blindsNow(g, Date.now());
        const preview = P.startHand(seats, stacks, pk.button, bl, pk.handNo + 1, pk.forceBtn);
        if (pk.last) put(box, lastHand(g, pk.last));
        if (preview.ok) {
            const pv = preview.hand;
            put(box, h('p', { class: 'bgb-hint', text: t('pk.next_hand', { d: H.name(g, pv.button), sb: H.name(g, pv.sbP), bb: H.name(g, pv.bbP) }) }));
        } else put(box, h('p', { class: 'bgb-warn', text: t('err.players') }));
        put(box, h('button', {
            type: 'button', class: 'lg-btn bgb-deal', id: 'bgbDeal', disabled: !preview.ok || !!g.ended,
            text: t('pk.deal', { n: pk.handNo + 1 }), on: { click: () => app.run({ type: 'hand_start' }) },
        }));
    } else {
        const ps = P.pots(hd);
        const potLine = h('div', { class: 'bgb-potline' },
            h('span', { class: 'bgb-street', text: t('pk.hand_n', { n: hd.n }) + ' · ' + streetName(hd) }),
            h('span', { class: 'bgb-pot-total', text: t('pk.pot_x', { x: num(P.potTotal(hd)) }) }),
            // side pots exist only once somebody is all-in; mid-round bets are just bets
            ps.length > 1 && hd.order.some((q) => hd.s[q].allin && !hd.s[q].fold) ? h('span', { class: 'bgb-hint', text: ps.map((p, i) => (i ? t('pk.side_n', { n: i }) : t('pk.main')) + ' ' + num(p.amount)).join(' · ') }) : null,
            hd.street !== 'show' && hd.cur ? h('span', { class: 'bgb-hint', text: t('pk.cur_bet', { x: num(hd.cur) }) }) : null);
        put(box, potLine);
        if (hd.street === 'show') put(box, showdown(app, g, hd, ps));
        else {
            const p = L.player(g, hd.toAct);
            const x = hd.s[hd.toAct];
            const o = P.options(hd, hd.toAct);
            put(box, h('div', { class: 'bgb-turn', style: { '--pc': p.color } },
                h('span', { class: 'bgb-turn-t' }, t('pk.turn') + ': ', h('b', { text: p.token + ' ' + p.name })),
                h('span', { class: 'bgb-hint', text: t('pk.stack_x', { x: num(x.stack) }) + (o && o.call ? ' · ' + t('pk.to_call', { x: num(o.callAmt) }) : '') + (p.dev && app.online.has(p.id) ? ' · 📱 ' + t('pk.on_phone') : '') })),
            actionNode(app, g, hd.toAct));
        }
    }
    put(el, box);
}

function lastHand(g, last) {
    const bits = [];
    for (const p of last.pots) for (const w of p.winners) bits.push(H.name(g, w) + ' +' + num(p.shares[w]));
    return h('p', { class: 'bgb-last', text: t('pk.last', { n: last.n }) + ': ' + bits.join(', ') + (last.fold ? ' · ' + t('h.all_folded') : '') });
}

function showdown(app, g, hd, ps) {
    const pick = ps.map((p) => (p.elig.length === 1 ? p.elig.slice() : []));
    const paints = [];
    const box = h('div', { class: 'bgb-show' });
    put(box, h('p', { class: 'bgb-p', text: hd.runout ? t('pk.runout') : t('pk.who_won') }));
    const go = h('button', { type: 'button', class: 'lg-btn', id: 'bgbAward', text: t('pk.award') });
    function valid() { return pick.every((w) => w.length > 0); }
    ps.forEach((pot, i) => {
        const chips = h('div', { class: 'bgb-cps', role: 'group', 'aria-label': (i ? t('pk.side_n', { n: i }) : t('pk.main')) });
        const paint = () => {
            put(clear(chips), pot.elig.map((pid) => {
                const p = L.player(g, pid);
                return h('button', {
                    type: 'button', class: 'bgb-cp', 'aria-pressed': String(pick[i].indexOf(pid) >= 0), style: { '--pc': p.color }, text: p.token + ' ' + p.name, 'data-win': pid,
                    disabled: pot.elig.length === 1,
                    on: {
                        click: () => {
                            const k = pick[i].indexOf(pid);
                            if (k >= 0) pick[i].splice(k, 1); else pick[i].push(pid);
                            // the winner of the main pot usually wins the side pots he is in too
                            if (k < 0) for (let j = i + 1; j < ps.length; j++) if (!pick[j].length && ps[j].elig.indexOf(pid) >= 0) { pick[j] = [pid]; paints[j](); }
                            paint();
                            go.disabled = !valid();
                        },
                    },
                });
            }));
        };
        paints.push(paint);
        put(box, h('div', { class: 'bgb-potpick' },
            h('div', { class: 'bgb-dlg-l', text: (i ? t('pk.side_n', { n: i }) : t('pk.main')) + ' · ' + num(pot.amount) + (pot.elig.length > 1 ? ' — ' + t('pk.tap_winner') : '') }), chips));
        paint();
    });
    go.disabled = !valid();
    go.addEventListener('click', () => app.run({ type: 'award', winners: pick }));
    put(box, h('p', { class: 'bgb-hint', text: t('pk.split_hint') }), go);
    return box;
}

/* ── the clock tick (text only, no re-render) ─────────────────────────────── */

export function tick(app) {
    const g = app.game;
    if (!g || g.kind !== 'poker') return;
    const at = P.timerAt(g.poker.timer, g.poker.levels, Date.now());
    document.querySelectorAll('[data-clock]').forEach((el) => { el.textContent = at.over ? '—' : mmss(at.remain); });
    if (app.lastLvl != null && at.lvl !== app.lastLvl) {
        const lv = g.poker.levels[at.lvl];
        app.onLevel(lv);
    }
    app.lastLvl = at.lvl;
}

